// frontend/src/pages/Projects.jsx
import React, { useState, useEffect } from 'react';
import {
  FolderKanban, Plus, Copy, Trash2, ChevronRight,
  Loader2, AlertTriangle, LogOut, Globe, Calendar,
  CheckCircle2, XCircle, Clock, Play, Layers, Menu,
} from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguageStore } from '../store/useLanguageStore';
import { useProjectStore } from '../store/useProjectStore';
import { LanguageSelector } from '../components/LanguageSelector';
import { Modal } from '../components/Modal';
import LogoHorizontal from '../assets/images/datiaFlow_horizontal.jpg';
import Isotipo from '../assets/images/datiaFlow_cuadrado.jpg';
import { AppSidebar } from '../components/AppSidebar';
import { useUIStore } from '../store/useUIStore';

// ── Helpers ────────────────────────────────────────────────────────

const STATUS_STYLES = {
  draft: 'bg-slate-100  text-slate-600  border-slate-200',
  active: 'bg-blue-50    text-blue-700   border-blue-200',
  running: 'bg-amber-50   text-amber-700  border-amber-200',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  archived: 'bg-slate-50   text-slate-400  border-slate-200',
};

const StatusIcon = ({ status }) => {
  const cls = 'w-3.5 h-3.5 mr-1';
  if (status === 'running') return <Loader2 className={`${cls} animate-spin`} />;
  if (status === 'completed') return <CheckCircle2 className={cls} />;
  if (status === 'draft') return <Clock className={cls} />;
  if (status === 'archived') return <XCircle className={cls} />;
  return <Globe className={cls} />;
};

const _toLocalDatetime = (ms) => {
  const d = new Date(ms);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${dd}T${h}:${min}`;
};

const CutoverBadge = ({ mode, cutoverAt, t }) => (
  <span className={`inline-flex items-center text-[10px] font-bold px-2 py-0.5 rounded border
    ${mode === 'scheduled'
      ? 'bg-violet-50 text-violet-700 border-violet-200'
      : 'bg-slate-50  text-slate-500  border-slate-200'}`}>
    <Calendar className="w-3 h-3 mr-1" />
    {t(`projects.cutoverMode.${mode}`) || mode}
    {mode === 'scheduled' && cutoverAt > 0 && (
      <span className="ml-1 font-normal">
        · {new Date(cutoverAt).toLocaleString(undefined, {
          day: '2-digit', month: '2-digit', year: 'numeric',
          hour: '2-digit', minute: '2-digit',
        })}
      </span>
    )}
  </span>
);

// ── Modal de creación / edición de proyecto ────────────────────────

const ProjectFormModal = ({ isOpen, project, onClose, onSave, onValidationError, t }) => {
  const [form, setForm] = useState({
    name: '', description: '', cutoverMode: 'manual', cutoverAt: 0,
  });
  const [saving, setSaving] = useState(false);
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setValidationError('');
      setForm(project
        ? {
          name: project.name, description: project.description || '',
          cutoverMode: project.cutoverMode, cutoverAt: project.cutoverAt,
          status: project.status
        }
        : { name: '', description: '', cutoverMode: 'manual', cutoverAt: 0 }
      );
    }
  }, [isOpen, project]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!form.name.trim()) return;
    // Validar que cutoverAt no sea en el pasado
    if (form.cutoverMode === 'scheduled' && form.cutoverAt > 0 && form.cutoverAt < Date.now()) {
      setValidationError(t('projects.cutoverAt_past') || 'La fecha del Día D no puede ser en el pasado.');
      return;
    }
    setValidationError('');
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <h3 className="text-lg font-black text-slate-900 mb-5">
          {project ? t('projects.edit') || 'Editar Proyecto' : t('projects.new')}
        </h3>

        <div className="space-y-4">
          {/* Nombre */}
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">
              {t('common.name') || 'Nombre'} *
            </label>
            <input
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              maxLength={100}
              autoComplete="off"
            />
          </div>

          {/* Descripción */}
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">
              {t('common.description') || 'Descripción'}
            </label>
            <textarea
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 resize-none"
              rows={3}
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              maxLength={500}
            />
          </div>

          {/* Modo Cutover */}
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">
              {t('projects.cutoverMode.label')}
            </label>
            <select
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
              value={form.cutoverMode}
              onChange={e => setForm(f => ({
                ...f,
                cutoverMode: e.target.value,
                cutoverAt: e.target.value === 'manual' ? 0 : f.cutoverAt,
              }))}
            >
              <option value="manual">{t('projects.cutoverMode.manual')}</option>
              <option value="scheduled">{t('projects.cutoverMode.scheduled')}</option>
            </select>
            {/* Fecha/hora de Día D — solo visible en modo programado */}
            {form.cutoverMode === 'scheduled' && (
              <div className="mt-3">
                <label className="block text-xs font-bold text-slate-600 mb-1">
                  {t('projects.cutoverAt') || 'Fecha del Día D'}
                </label>
                <input
                  type="datetime-local"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  value={form.cutoverAt
                    // ? new Date(form.cutoverAt).toISOString().slice(0, 16)
                    ? _toLocalDatetime(form.cutoverAt)
                    : ''
                  }
                  onChange={e => {
                    const ms = e.target.value ? new Date(e.target.value).getTime() : 0;
                    setForm(f => ({ ...f, cutoverAt: ms }));
                    setValidationError('');
                  }}
                  // min={new Date().toISOString().slice(0, 16)}
                  min={_toLocalDatetime(Date.now())}
                />
                <p className="text-[10px] text-slate-400 mt-1">
                  {t('projects.cutoverAt_hint') || 'Recibirás una alerta 24 horas antes de esta fecha.'}
                  {' · '}
                  {Intl.DateTimeFormat().resolvedOptions().timeZone}
                </p>
              </div>
            )}
          </div>
          {/* Status — solo visible al editar, no al crear */}
          {project && (
            <div className="col-span-2">
              <label className="block text-xs font-bold text-slate-600 mb-1">
                {t('common.status') || 'Estado'}
              </label>
              <select
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                value={form.status}
                onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
              >
                {project.status === 'draft' && (
                  <>
                    <option value="draft">{t('projects.status.draft') || 'Borrador'}</option>
                    <option value="active">{t('projects.status.active') || 'Activo'}</option>
                  </>
                )}
                {project.status === 'active' && (
                  <>
                    <option value="active">{t('projects.status.active') || 'Activo'}</option>
                    <option value="draft">{t('projects.status.draft') || 'Borrador'}</option>
                  </>
                )}
                {project.status === 'completed' && (
                  <>
                    <option value="completed">{t('projects.status.completed') || 'Completado'}</option>
                    <option value="active">{t('projects.status.active') || 'Activo'}</option>
                  </>
                )}
              </select>
            </div>
          )}
        </div>

        {validationError && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-700 font-medium mt-4">
            {validationError}
          </div>
        )}

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-sm font-bold text-slate-600 hover:bg-slate-100 transition-colors"
          >
            {t('common.buttons.cancel') || 'Cancelar'}
          </button>
          <button
            onClick={handleSave}
            disabled={!form.name.trim() || saving}
            className="px-5 py-2 rounded-xl text-sm font-bold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {t('common.buttons.save') || 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Card de proyecto ───────────────────────────────────────────────

const ProjectCard = ({ project, onEdit, onCopy, onDelete, navigate, t }) => {
  const phaseCount = project.phases?.length ?? '—';
  const statusStyle = STATUS_STYLES[project.status] || STATUS_STYLES.draft;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow p-5 flex flex-col gap-4">

      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center shrink-0">
            <FolderKanban className="w-5 h-5 text-indigo-600" />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-black text-slate-900 leading-tight truncate">
              {project.name}
            </h3>
            {project.description && (
              <p className="text-xs text-slate-500 truncate mt-0.5">{project.description}</p>
            )}
          </div>
        </div>

        {/* Badge de estado */}
        <span className={`inline-flex items-center text-[10px] font-bold px-2 py-1 rounded-full border shrink-0 ${statusStyle}`}>
          <StatusIcon status={project.status} />
          {t(`projects.status.${project.status}`) || project.status}
        </span>
      </div>

      {/* Métricas */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-1.5">
          <Layers className="w-3.5 h-3.5 text-slate-400" />
          <span className="text-xs text-slate-500">
            <span className="font-bold text-slate-700">{phaseCount}</span> {t('projects.phaseName') || 'fase(s)'}
          </span>
        </div>
        <CutoverBadge mode={project.cutoverMode} cutoverAt={project.cutoverAt} t={t} />
      </div>

      {/* Acciones */}
      <div className="flex items-center justify-between border-t border-slate-100 pt-3">
        <div className="flex items-center gap-1">
          <button
            onClick={() => onEdit(project)}
            className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
            title={t('common.edit') || 'Editar'}
          >
            <FolderKanban className="w-4 h-4" />
          </button>
          <button
            onClick={() => onCopy(project)}
            className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
            title={t('projects.copy.confirm') || 'Copiar'}
          >
            <Copy className="w-4 h-4" />
          </button>
          {project.status === 'draft' && (
            <button
              onClick={() => onDelete(project)}
              className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
              title={t('common.delete') || 'Eliminar'}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>

        <button
          onClick={() => navigate(`/projects/${project.idProject}`)}
          className="flex items-center gap-1 text-xs font-bold text-indigo-600 hover:text-indigo-800 transition"
        >
          {t('common.view') || 'Ver detalle'} <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// ── Página principal ───────────────────────────────────────────────

export const Projects = ({ user, setUser }) => {
  const navigate = useNavigate();
  const { t } = useLanguageStore();
  const {
    projects, isLoadingList, isSaving, error,
    fetchProjects, createProject, updateProject, deleteProject, copyProject,
    clearError,
  } = useProjectStore();

  const [formModal, setFormModal] = useState({ open: false, project: null });
  const [modalConfig, setModalConfig] = useState({ isOpen: false });

  const closeModal = () => setModalConfig({ isOpen: false });
  // const [statusFilter, setStatusFilter] = useState('active');
  const [statusFilter, setStatusFilter] = useState('all');
  const { openSidebar } = useUIStore();

  useEffect(() => { fetchProjects(); }, []);

  const handleLogout = () => {
    localStorage.removeItem('vnext_auth_token');
    localStorage.removeItem('vnext_user');
    setUser(null);
    navigate('/login');
  };

  // ── Guardar (crear o editar) ─────────────────────────────────────
  const handleSaveProject = async (formData) => {
    const { project } = formModal;
    const result = project
      ? await updateProject(project.idProject, formData)
      : await createProject(formData);

    if (result.ok) {
      setFormModal({ open: false, project: null });
    } else {
      setModalConfig({
        isOpen: true, showCancel: false,
        title: t('common.error') || 'Error',
        message: result.error,
        onAccept: closeModal,
      });
    }
  };

  // ── Copiar ────────────────────────────────────────────────────────
  const handleCopy = (project) => {
    setModalConfig({
      isOpen: true, showCancel: true,
      title: t('dashboard.actions.copy_title') || 'Copiar Proyecto',
      message: `${t('projects.copy.confirm')} "${project.name}"?`,
      onAccept: async () => {
        closeModal();
        const result = await copyProject(project.idProject);
        if (!result.ok) {
          setModalConfig({
            isOpen: true, showCancel: false,
            title: t('common.error') || 'Error',
            message: result.error,
            onAccept: closeModal,
          });
        }
      },
    });
  };

  // ── Eliminar ──────────────────────────────────────────────────────
  const handleDelete = (project) => {
    setModalConfig({
      isOpen: true, showCancel: true,
      title: t('common.delete') || 'Eliminar Proyecto',
      message: `¿Eliminar "${project.name}"? Esta acción no se puede deshacer.`,
      onAccept: async () => {
        closeModal();
        const result = await deleteProject(project.idProject);
        if (!result.ok) {
          setModalConfig({
            isOpen: true, showCancel: false,
            title: t('common.error') || 'Error',
            message: result.error,
            onAccept: closeModal,
          });
        }
      },
    });
  };

  // ── Render ────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#F8FAFC]">

      {/* ── HEADER ──────────────────────────────────────────────── */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={openSidebar}
              aria-label="Abrir menú"
              className="p-2 rounded-xl text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 transition-colors shrink-0"
            >
              <Menu className="w-5 h-5" />
            </button>
            <Link to="/dashboard" className="flex items-center cursor-pointer group shrink-0">
              <img src={Isotipo} alt="DatiaFlow"
                className="block md:hidden h-9 w-9 object-cover object-left rounded-md group-hover:scale-105 transition-transform" />
              <img src={LogoHorizontal} alt="DatiaFlow"
                className="hidden md:block h-11 w-auto group-hover:opacity-90 transition-opacity" />
            </Link>
          </div>

          {/* Nav interna */}
          <nav className="hidden md:flex items-center gap-6 text-sm font-semibold">
            <Link to="/dashboard" className="text-slate-500 hover:text-indigo-600 transition">
              {t('dashboard.title') || 'Control ETL'}
            </Link>
            <span className="text-indigo-600 border-b-2 border-indigo-600 pb-0.5">
              {t('projects.title')}
            </span>
          </nav>

          <div className="flex items-center space-x-6">
            <LanguageSelector />
            <div className="flex items-center space-x-3 border-l border-slate-200 pl-6">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-bold text-slate-700">{user?.name}</p>
              </div>
              <button onClick={handleLogout}
                className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                title={t('dashboard.logout') || 'Cerrar sesión'}>
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* ── MAIN ─────────────────────────────────────────────────── */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-in fade-in">

        {/* Título + Filtro + CTA */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center">
              <FolderKanban className="w-6 h-6 mr-2 text-indigo-600" />
              {t('projects.title')}
            </h1>
            <p className="text-sm text-slate-500 mt-1">{t('projects.subtitle')}</p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400"
            >
              <option value="all">{t('projects.status.all') || 'Todos'}</option>
              <option value="active">{t('projects.status.active') || 'Activos'}</option>
              <option value="draft">{t('projects.status.draft') || 'Borradores'}</option>
              <option value="completed">{t('projects.status.completed') || 'Completados'}</option>
              <option value="archived">{t('projects.status.archived') || 'Archivados'}</option>
            </select>
            <button
              onClick={() => setFormModal({ open: true, project: null })}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-xl font-bold text-sm shadow-sm transition flex items-center justify-center gap-1"
            >
              <Plus className="w-5 h-5" /> {t('projects.new')}
            </button>
          </div>
        </div>

        {/* Error global */}
        {error && (
          <div className="mb-6 flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-700 text-sm">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            {error}
            <button onClick={clearError} className="ml-auto text-red-400 hover:text-red-600">✕</button>
          </div>
        )}

        {/* Loading */}
        {isLoadingList ? (
          <div className="flex justify-center items-center py-24">
            <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
          </div>
        ) : (() => {
          const filteredProjects = statusFilter === 'all'
            ? projects
            : statusFilter === 'active'
              ? projects.filter(p => p.status === 'active' || p.status === 'running')
              : projects.filter(p => p.status === statusFilter);

          return filteredProjects.length === 0 ? (
            /* Empty state — con contexto del filtro */
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm py-16 flex flex-col items-center">
              <div className="w-14 h-14 rounded-2xl bg-slate-50 flex items-center justify-center mb-4">
                <FolderKanban className="w-7 h-7 text-slate-300" />
              </div>
              <p className="text-slate-500 text-sm mb-1">
                {t('projects.empty_filter') || 'No hay proyectos con este filtro.'}
              </p>
              {statusFilter !== 'all' && (
                <button
                  onClick={() => setStatusFilter('all')}
                  className="text-xs text-indigo-600 hover:text-indigo-800 font-bold mt-2 transition"
                >
                  {t('common.show_all') || 'Ver todos los proyectos'}
                </button>
              )}
            </div>
          ) : (
            /* Grid de cards */
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
              {filteredProjects.map(p => (
                <ProjectCard
                  key={p.idProject}
                  project={p}
                  navigate={navigate}
                  t={t}
                  onEdit={(proj) => setFormModal({ open: true, project: proj })}
                  onCopy={handleCopy}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          );
        })()}
      </main>

      {/* ── MODALES ──────────────────────────────────────────────── */}
      <ProjectFormModal
        isOpen={formModal.open}
        project={formModal.project}
        onClose={() => setFormModal({ open: false, project: null })}
        onSave={handleSaveProject}
        t={t}
      />

      <Modal
        isOpen={modalConfig.isOpen}
        title={modalConfig.title}
        message={modalConfig.message}
        onClose={closeModal}
        onAccept={modalConfig.onAccept}
        showCancel={modalConfig.showCancel}
      />
      <AppSidebar user={user} onLogout={handleLogout} />
    </div>
  );
};

export default Projects;
